/**
 * Browser API client — cookie-session mode (spec §4.4).
 *
 * Ported from penteratracking `lib/api/client.ts`, adapted for the IsmsHub BFF:
 * the browser never holds a JWT. Every request goes to the Next.js proxy
 * (`/api/proxy/*`) with `credentials: 'include'`; the BFF attaches the Bearer
 * token and the `X-IsmsHub-Tenant` header from the server-side session. The
 * sessionStorage token store, `ensureFreshToken`, and proactive refresh
 * machinery are dropped. On a 401 the client emits `auth:session-expired`, which
 * the shell listens for to redirect to login.
 *
 * The response contract itself (error parsing, the 401 signal, empty-body
 * tolerance) lives in `lib/api/http.ts` and is shared with `lib/api/bff.ts`,
 * the sibling client for same-origin Next route handlers (backlog D19).
 */

import {
  fetchOrTransportError,
  readJsonOrEmpty,
  throwApiError,
  type ApiCallOptions,
} from "./http";
import { withExpectedTenantHeader } from "./tenant-assertion";

// Re-exported because `ApiError`/`ApiCallOptions` have always been imported
// from this module; moving 56 call sites would buy nothing.
export { ApiError } from "./http";
export type { ApiCallOptions } from "./http";

/**
 * Base URL of the BFF proxy. All app traffic is same-origin. Includes the API
 * version segment so hooks keep the source repo's unversioned paths
 * ("/isms/frameworks") — the proxy maps /api/proxy/v1/* → backend /api/v1/*.
 */
const API_BASE = "/api/proxy/v1";

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = withExpectedTenantHeader({
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  });

  const response = await fetchOrTransportError(`${API_BASE}${path}`, {
    ...options,
    credentials: "include",
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  return readJsonOrEmpty<T>(response);
}

async function requestFormData<T>(path: string, formData: FormData): Promise<T> {
  // Do NOT set Content-Type — the browser sets it with the correct multipart boundary.
  const response = await fetchOrTransportError(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: withExpectedTenantHeader(),
    body: formData,
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  return readJsonOrEmpty<T>(response);
}

async function requestText<T>(path: string, text: string, contentType: string): Promise<T> {
  const response = await fetchOrTransportError(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: withExpectedTenantHeader({ "Content-Type": contentType }),
    body: text,
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  return readJsonOrEmpty<T>(response);
}

async function requestBlob(path: string): Promise<Blob> {
  const response = await fetchOrTransportError(`${API_BASE}${path}`, {
    method: "GET",
    credentials: "include",
    headers: withExpectedTenantHeader(),
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  return response.blob();
}

/** A downloaded file with the server-suggested name (Content-Disposition). */
export interface NamedBlob {
  blob: Blob;
  filename: string | null;
}

/** Parses the `filename`/`filename*` token of a Content-Disposition header. */
export function parseContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, ""));
    } catch {
      return star[1].trim().replace(/^["']|["']$/g, "");
    }
  }
  const plain = /filename=("?)([^";]+)\1/i.exec(header);
  return plain?.[2]?.trim() ?? null;
}

/**
 * POST that returns a downloadable file (e.g. report/export endpoints that take
 * a JSON body and respond with a FileContentResult). Surfaces the
 * server-suggested filename from the Content-Disposition header.
 */
async function postBlob(path: string, body?: unknown): Promise<NamedBlob> {
  const response = await fetchOrTransportError(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: withExpectedTenantHeader({ "Content-Type": "application/json" }),
    body: body != null ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  return {
    blob: await response.blob(),
    filename: parseContentDisposition(response.headers.get("content-disposition")),
  };
}

/** Silent request that returns null for 404/204 instead of throwing (e.g. preferences). */
async function requestOrNull<T>(path: string, options: RequestOptions = {}): Promise<T | null> {
  const headers: Record<string, string> = withExpectedTenantHeader({
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  });

  const response = await fetchOrTransportError(`${API_BASE}${path}`, {
    ...options,
    credentials: "include",
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (response.status === 404 || response.status === 204) {
    return null;
  }

  if (!response.ok) {
    await throwApiError(response);
  }

  return readJsonOrEmpty<T>(response);
}

export const api = {
  get: <T>(path: string, opts?: ApiCallOptions) =>
    request<T>(path, { method: "GET", signal: opts?.signal }),
  // Silent GET that returns null for 404 instead of throwing.
  getOrNull: <T>(path: string, opts?: ApiCallOptions) =>
    requestOrNull<T>(path, { method: "GET", signal: opts?.signal }),
  getBlob: (path: string) => requestBlob(path),
  postBlob: (path: string, body?: unknown) => postBlob(path, body),
  post: <T>(path: string, body?: unknown, opts?: ApiCallOptions) =>
    request<T>(path, { method: "POST", body, signal: opts?.signal }),
  put: <T>(path: string, body?: unknown, opts?: ApiCallOptions) =>
    request<T>(path, { method: "PUT", body, signal: opts?.signal }),
  patch: <T>(path: string, body?: unknown, opts?: ApiCallOptions) =>
    request<T>(path, { method: "PATCH", body, signal: opts?.signal }),
  delete: <T>(path: string, opts?: ApiCallOptions) =>
    request<T>(path, { method: "DELETE", signal: opts?.signal }),
  postFormData: <T>(path: string, formData: FormData) => requestFormData<T>(path, formData),
  postText: <T>(path: string, text: string, contentType: string) =>
    requestText<T>(path, text, contentType),
};
